import { UIIconColors } from '../../components/icon/ui-icon.helpers';
import { UIIcon } from '../../components/icon/ui-icon';
import { UITable } from '../../components/table/ui-table';
import type { ComponentPlugin } from '../../components/ui-element.type';
import { Digest } from '../../global/digest';
import { scrollTo } from '../../global/helpers';
import { Labels } from '../../global/labels';
import {
    closestByCond,
    createElement,
    updateElement,
} from '../../global/ui-helpers';
import type { NullOr } from '../../global/utils.type';
import type { TranslationLabels } from '../../types/labels.type';
import type { UITableExpandableGroup } from './ui-table-expandable.type';

/**
 * @class
 * @memberof SharedComponents
 * @classdesc Class for ui-table auto plugin.
 * @implements {Activatable}
 * @alias UITableExpandable
 * @implements {ComponentPlugin}
 * @param {HTMLTableElement} target Target instance.
 */
export class UITableExpandable implements ComponentPlugin {
    private readonly target: UITable;

    constructor(target: UITable) {
        this.target = target;
    }

    /**
     * @type {TranslationLabels.Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-table-expandable', {
            expandRow: 'Expand row',
            collapseRow: 'Collapse row',
            actions: 'Actions',
        });
    }

    /**
     * Checks if row can be expanded.
     * @param {HTMLElement} elem
     * @returns {boolean}
     * @private
     */
    private canExpand(elem: HTMLElement): boolean {
        return (
            ['td', 'th'].includes(elem.localName) ||
            elem.classList.contains('fixed-title') ||
            closestByCond(elem, (node) => {
                return (
                    node instanceof HTMLElement &&
                    node.localName === 'button' &&
                    node.classList.contains('expander-button')
                );
            }) !== null
        );
    }

    /**
     * Changes current view to active (open) state.
     * @param {string} [rowId] Row id to be expanded, if not set window.location.hash is used.
     * @returns {HTMLTableRowElement}
     */
    async activate(rowId?: string): Promise<NullOr<HTMLElement>> {
        const id = window.location.hash.replace('#', '') || rowId;
        const row = document.getElementById(id!);
        if (row && !row.classList.contains('-open')) {
            try {
                row.querySelector<HTMLButtonElement>(
                    'button.expander-button'
                )?.click();
            } catch {}
            await scrollTo(row, 0);
        }
        return row;
    }

    /**
     * @param {HTMLTableRowElement} row
     * @param {boolean} [open]
     * @private
     */
    private toggleExtraRows(
        row: HTMLTableRowElement,
        open: boolean = false
    ): void {
        let next = row.nextElementSibling;
        while (next) {
            // We search only until next expandable row.
            if (next.classList.contains('-expand')) {
                break;
            }

            open
                ? next.classList.add('-expanded')
                : next.classList.remove('-expanded');
            next = next.nextElementSibling;
        }
    }

    /**
     * @param {Element} row
     * @returns {boolean}
     * @private
     */
    private isRowOpened(row: Element): boolean {
        return row.classList.contains('-open');
    }

    /**
     * @param {Element} row
     * @returns {boolean}
     * @private
     */
    private hasNextExtra(row: Element): boolean {
        let ret = false;
        let next = row.nextElementSibling;
        while (next) {
            if (next.classList.contains('-extra')) {
                ret = true;
                break;
            }
            next = next.nextElementSibling;
        }
        return ret;
    }

    /**
     * Returns grouped table rows and expand/collapse control buttons.
     * @returns {Array<UITableExpandableGroup>}
     * @private
     */
    private getGroupedExpandElements(): Array<UITableExpandableGroup> {
        return [
            ...this.target.querySelectorAll<HTMLTableRowElement>('tbody tr'),
        ].reduce<Array<UITableExpandableGroup>>((accumulator, currentValue) => {
            if (currentValue.classList.contains('-expand')) {
                accumulator.push({
                    button: currentValue.querySelector('.expander-button'),
                    expandableRow: currentValue,
                    extraRows: [],
                });
            } else if (
                accumulator.length &&
                currentValue.classList.contains('-extra')
            ) {
                accumulator.at(-1)?.extraRows?.push(currentValue);
            }

            return accumulator;
        }, []);
    }

    /**
     * Collapses a row
     * @param {HTMLButtonElement} controlButton
     * @param {HTMLTableRowElement} expandableRow
     * @param {Array<HTMLTableRowElement>} extraRows
     * @returns {void}
     * @private
     */
    private collapseRow(
        controlButton: NullOr<HTMLButtonElement>,
        expandableRow: HTMLTableRowElement,
        extraRows: Array<HTMLTableRowElement>
    ): void {
        const icon = controlButton?.querySelector<UIIcon>('ui-icon');
        const controlButtonLabel = UITableExpandable.labels.expandRow as string;

        if (icon) {
            icon.glyph = 'down';
            icon.color = UIIconColors.ORANGE;
        }
        expandableRow.classList.remove('-open');
        extraRows.forEach((row) => row.classList.remove('-expanded'));
        controlButton?.setAttribute('aria-label', controlButtonLabel);
        controlButton?.setAttribute('aria-expanded', 'false');
    }

    /**
     * Expands a row
     * @param {HTMLButtonElement} controlButton
     * @param {HTMLTableRowElement} expandableRow
     * @param {Array<HTMLTableRowElement>} extraRows
     * @returns {void}
     * @private
     */
    private expandRow(
        controlButton: NullOr<HTMLButtonElement>,
        expandableRow: HTMLTableRowElement,
        extraRows: Array<HTMLTableRowElement>
    ): void {
        const icon = controlButton?.querySelector<UIIcon>('ui-icon');
        const controlButtonLabel = UITableExpandable.labels
            .collapseRow as string;

        if (icon) {
            icon.glyph = 'up';
            icon.color = UIIconColors.BARK_70;
        }
        expandableRow.classList.add('-open');
        extraRows.forEach((row) => row.classList.add('-expanded'));
        controlButton?.setAttribute('aria-label', controlButtonLabel);
        controlButton?.setAttribute('aria-expanded', 'true');
    }

    /**
     * @inheritDoc
     */
    render(): void {
        const uid = Digest.randomId();
        const isComparison = !!this.target.getInstancePlugins().comparison;
        const rows = this.target.querySelectorAll('tbody tr.-expand');

        this.target.table.classList.add('-expandable');
        if (rows.length > 0 && this.target.table.tHead) {
            const row = this.target.table.tHead.rows[0];
            if (row && !isComparison) {
                const th = createElement({
                    tagName: 'th',
                    children: [
                        {
                            tagName: 'span',
                            classList: {
                                '-visually-hidden': true,
                            },
                            children: [UITableExpandable.labels.actions],
                        },
                    ],
                })!;
                row.appendChild(th);
            }
        }

        const expanderCell = createElement({
            tagName: 'td',
            classList: {
                'expander': true,
                'col': true,
                '-last': true,
            },
        });

        // If there aren't next row with class .row.-extra, just do not
        // add the expand button to it.
        for (const row of rows) {
            const tableRow = row as HTMLTableRowElement;
            const isOpened = this.isRowOpened(tableRow);
            const hasNextExtraRow = this.hasNextExtra(tableRow);

            let next = row.nextElementSibling;
            while (next) {
                // We search only until next expandable row.
                if (next.classList.contains('-expand')) {
                    break;
                }

                isOpened
                    ? next.classList.add('-expanded')
                    : next.classList.remove('-expanded');
                next = next.nextElementSibling;
            }

            const button = createElement({
                tagName: 'button',
                classList: {
                    'expander-button': true,
                },
                attributes: {
                    'type': 'button',
                    'aria-label': isOpened
                        ? UITableExpandable.labels.collapseRow
                        : UITableExpandable.labels.expandRow,
                    'aria-expanded': isOpened ? 'true' : 'false',
                },
                children: [
                    {
                        tagName: 'ui-icon',
                        attributes: {
                            glyph: isOpened ? 'up' : 'down',
                            color: isOpened
                                ? UIIconColors.BARK_70
                                : UIIconColors.ORANGE,
                        },
                    },
                ],
            })!;

            const cell = expanderCell?.cloneNode(false);
            if (hasNextExtraRow) {
                updateElement(cell as HTMLElement, {
                    children: [button],
                });
            }

            if (isComparison) {
                tableRow.cells[0].appendChild(button);
            } else if (!row.classList.contains('-sub')) {
                row.appendChild(cell!);
            }
        }

        this.getGroupedExpandElements().forEach(
            ({ button, expandableRow, extraRows }, expandIndex) => {
                extraRows.forEach((extra, extraIndex) => {
                    extra.id ||= `${uid}-${expandIndex}-${extraIndex}`;
                });

                const extraIds = extraRows.map((extra) => extra.id).join(' ');
                button?.setAttribute('aria-controls', extraIds);

                const isMarkedAsOpen = this.isRowOpened(expandableRow);
                if (isMarkedAsOpen) {
                    this.expandRow(button, expandableRow, extraRows);
                }
            }
        );

        const nonExpandableRows = this.target.querySelectorAll(
            'tbody tr:not(.-expand)'
        );
        nonExpandableRows.forEach((row) => {
            row.appendChild(expanderCell!.cloneNode(false));
        });
    }

    /**
     * @inheritDoc
     */
    hydrate(): void {
        this.getGroupedExpandElements().forEach(
            ({ button, expandableRow, extraRows }) => {
                expandableRow.addEventListener('click', (e) => {
                    const isMarkedAsOpen = this.isRowOpened(expandableRow);

                    if (!this.canExpand(e.target as HTMLElement)) {
                        return;
                    }

                    if (isMarkedAsOpen) {
                        this.collapseRow(button, expandableRow, extraRows);
                    } else {
                        this.expandRow(button, expandableRow, extraRows);
                    }
                });
            }
        );

        void (
            this.target.getPlugin('expandable') as UITableExpandable
        )?.activate();
    }
}

UITable.registerPlugin('expandable', UITableExpandable);
